import { useEffect, useMemo } from 'react';

import type { Account } from '@shared/products-types';
import { useApiSliceSelector } from '@luna/api';

import { getChangeAliasFunctionByProductType } from '../services/changeAliasService';
import { useProducts } from './useProducts';
import { useLastNonBlankId } from './useLastNonBlankId';
import { getProductMethodsOrDefault } from '../services/productsFacadeService';
import type { AliasChange } from '../types/aliasChange';
import type {
    AccountOrAccountOfExternalProduct,
    ProductWithHelpers,
} from '../types/productHelpers';

export type UseProductDataReturn<BaseType, UnderlyingData = unknown> = {
    product?: ProductWithHelpers<BaseType, UnderlyingData>;
    refetch: () => Promise<any>;
    updateAlias: AliasChange;
    isPending: boolean;
    error: any;
};

/** @deprecated do not introduce more usages of this hook in @retail or @business packages! */
export const useProductData = <
    BaseType extends AccountOrAccountOfExternalProduct = Account,
    UnderlyingData = unknown,
>(
    id: string,
): UseProductDataReturn<BaseType, UnderlyingData> => {
    const {
        products,
        productsById,
        isPending: isProductsPending,
    } = useProducts<BaseType, UnderlyingData>();
    const currentNonBlankId = useLastNonBlankId(id);
    const product = useMemo(
        () => productsById[currentNonBlankId],
        [productsById, currentNonBlankId],
    );

    const updateAlias = useMemo(() => getChangeAliasFunctionByProductType(product), [product]);

    const productMethods = useMemo(() => {
        return getProductMethodsOrDefault(product, products);
    }, [products, product]);

    useEffect(() => {
        productMethods.queryIfNeeded?.();
    }, [productMethods]);

    const { isProductPending, error } = useApiSliceSelector(productMethods.stateSelector, [
        productMethods.detailSliceId,
    ]);

    return {
        product,
        refetch: productMethods.refetch,
        isPending: isProductPending || isProductsPending || false,
        updateAlias,
        error,
    };
};
